<template>
  <div
    :id="formattedId"
    :class="
      bem({
        background: props.block.contentLoopSectionFormatting?.background,
      })
    "
  >
    <div :class="bem('title-wrapper')">
      <p :class="bem('pretitle')">
        {{ props.block.pretitleOverride || activeProduct.name }}
      </p>
      <VisionHeading>
        <span v-if="props.block.sectionTitle">
          {{ props.block.sectionTitle }}
        </span>
        <template v-else>
          <span>{{ activeProduct.frameChoice.name }} with </span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="activeProduct.lensChoice.name" />
        </template>
      </VisionHeading>
    </div>
    <div :class="bem('gallery-wrapper')">
      <Swiper
        :class="bem('gallery')"
        v-bind="swiperOptions"
      >
        <SwiperSlide
          v-for="(viewAngle, index) in viewAngles"
          :key="index"
          :class="bem('slide')"
        >
          <div :class="bem('img-wrapper')">
            <StaticThreeSixtyFrame
              :key="`static-image-360-${viewAngle}`"
              :scale="customImageScaling[viewAngle]"
              :customised-product="activeProduct"
              :frame-number="getFrameNumber(viewAngle)"
              :show-secondary="viewAngle === 5"
              :show-background="false"
            />
          </div>
        </SwiperSlide>
      </Swiper>
      <div
        v-show="productData?.length >= 3"
        :class="bem('gallery-controls')"
      >
        <div :class="bem('gallery-control')">
          <CarouselArrow
            arrow="left"
            size="stretch"
            :background-colour="
              props.block.contentLoopSectionFormatting?.background === 'dark'
                ? 'white'
                : 'dark-grey'
            "
          />
        </div>
        <div :class="bem('gallery-control')">
          <CarouselArrow
            arrow="right"
            size="stretch"
            :background-colour="
              props.block.contentLoopSectionFormatting?.background === 'dark'
                ? 'white'
                : 'dark-grey'
            "
          />
        </div>
      </div>
    </div>
    <div :class="bem('controls-container')">
      <ProductCalloutControls
        v-if="productData?.length"
        :products="productData"
        :active-product="activeProduct"
        @update-active-product="handleUpdateActiveProduct"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'

interface ProductCalloutSectionProps {
  block: {
    id: string
    sectionTitle: string
    pretitleOverride: string
    products: {
      model: {
        name: string
        sku: string
      }
      heroDesign: {
        parts: {
          sku: string
        }[]
      }
    }[]
    contentLoopSectionFormatting: {
      background: string
    }
  }
  rowPosition: number
}

const props = defineProps<ProductCalloutSectionProps>()

const { $product } = useNuxtApp()

const bem = useBEM('ProductCalloutSection')

const activeProductSku = ref('')
const activeProduct = ref()

const viewAngleMapping = {
  1: 34,
  2: 14,
  3: 58,
  4: 49,
  5: 1,
  6: 46,
}

const customImageScaling = {
  1: 100,
  2: 120,
  3: 115,
  4: 115,
  5: 115,
  6: 115,
}

function getFrameNumber(viewAngle) {
  return viewAngleMapping[viewAngle] || 1
}

const variantsToShow = computed(() => {
  return props.block.products.map((product) => {
    return {
      baseSku: product.model.sku,
      sku:
        product.model.sku +
        '-' +
        product.heroDesign.parts.map((part) => part.sku).join('-'),
    }
  })
})

const productData = (
  await Promise.allSettled(
    variantsToShow.value.map((variant) => {
      return $product.getCustomisedProduct(variant.baseSku, {
        sku: variant.sku,
      })
    })
  )
)
  .map((result) =>
    result.status === 'fulfilled' && result.value ? result.value : null
  )
  .filter((a) => a)

if (!activeProductSku.value) {
  activeProductSku.value = productData[0]?.sku
  activeProduct.value = productData[0]
} else {
  activeProduct.value = productData.find(
    (product) => product.sku === activeProductSku.value
  )
}

function handleUpdateActiveProduct(newActiveProductSku) {
  activeProductSku.value = newActiveProductSku
}

watch(
  () => activeProductSku.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      activeProduct.value = productData.find(
        (product) => product.sku === newValue
      )
    }
  }
)

const viewAngles = [5, 1, 2, 5, 1, 2]
const totalItems = viewAngles.length
const xlDesktopLoop = 2.5
const desktopLoop = 2
const tabletLoop = 1.5
const mobileLoop = 1
const isLoopedXlDesktop = totalItems >= desktopLoop * 2
const isLoopedDesktop = totalItems >= desktopLoop * 2
const isLoopedTablet = totalItems >= tabletLoop * 2
const isLoopedMobile = totalItems >= mobileLoop * 2

const formattedId =
  'product-callout-section--' +
  props.block.id?.toLowerCase().replace(/\s+/g, '-')

const swiperOptions = ref({
  slidesPerView: mobileLoop,
  spaceBetween: 12,
  speed: 300,
  centeredSlides: isLoopedMobile,
  loop: isLoopedMobile,
  loopAdditionalSlides: totalItems,
  slideToClickedSlide: true,
  modules: [Navigation],
  navigation: {
    nextEl: `#${formattedId} [data-carousel-arrow="right"]`,
    prevEl: `#${formattedId} [data-carousel-arrow="left"]`,
  },
  breakpoints: {
    650: {
      slidesPerView: tabletLoop,
      loop: isLoopedTablet,
      centeredSlides: isLoopedTablet,
    },
    1200: {
      slidesPerView: desktopLoop,
      loop: isLoopedDesktop,
    },
    1440: {
      slidesPerView: xlDesktopLoop,
      loop: isLoopedXlDesktop,
    },
  },
})
</script>

<style lang="postcss" scoped>
.ProductCalloutSection {
  @apply w-full relative;

  --controls-height: 100px;

  &--background-dark {
    .ProductCalloutSection__title-wrapper {
      @apply text-white;
    }
  }
}

.ProductCalloutSection__controls-container {
  @apply absolute inset-0 flex items-end justify-center pointer-events-none z-[8];
}

.ProductCalloutSection__gallery-controls {
  @apply absolute top-1/2 left-0 -translate-y-1/2 z-[7]
    w-full
    flex justify-between
    px-2 sm:px-6 md:px-10
    pointer-events-none;
}

.ProductCalloutSection__gallery-control {
  @apply w-5 sm:w-10
    pointer-events-auto;
}

.ProductCalloutSection__gallery {
  @apply pb-[var(--controls-height)];
}

.ProductCalloutSection__slide {
  @apply flex items-center justify-center;
}

.ProductCalloutSection__img-wrapper {
  @apply aspect-square
    relative w-full;
}

.ProductCalloutSection__title-wrapper {
  @apply text-center smMax:max-w-80 smMax:mx-auto;

  padding-left: var(--outer-gutter);
  padding-right: var(--outer-gutter);
}

.ProductCalloutSection__pretitle {
  @apply font-mono text-xxs mb-6;
}
</style>
